/**
 * Barcode scanning for the tablet camera (BP-02 §6 `/inventory/receive`, rule 16).
 *
 * §6 asks for "`BarcodeScanner` (`lib/camera`: `BarcodeDetector` API, fallback
 * `@zxing/browser`)" with the state "camera denied → manual entry". This module is the
 * non-React half of that: feature detection, the camera request, the scan loop, and the
 * EAN checksum — all injectable, so the React component has no branch of its own that a
 * test cannot reach.
 *
 * ## What the platform gives us, and what it does not
 *
 * `BarcodeDetector` (https://developer.mozilla.org/en-US/docs/Web/API/BarcodeDetector) is
 * a **secure-context, limited-availability** API: it is in Android Chrome, which is the
 * salon tablet (BP-02 §11 "Manual (salon)": "Amit scans 5 real bottles with the tablet
 * camera (Android Chrome)"), and absent in Firefox and in desktop Safari. MDN's own
 * feature-detection story is `if (!("BarcodeDetector" in globalThis))`, and
 * `BarcodeDetector.getSupportedFormats()` then says which symbologies that
 * implementation can actually read — a browser may have the constructor and not `ean_8`.
 * Both checks are here, in that order, because constructing a detector for an
 * unsupported format throws.
 *
 * `detect(imageBitmapSource)` resolves an array of `DetectedBarcode`
 * (`rawValue`, `format`, `boundingBox`, `cornerPoints`) and **rejects** with
 * `InvalidStateError` while a `<video>` is still at `HAVE_NOTHING`/`HAVE_METADATA` — so
 * the loop below treats a rejected frame as "no code in this frame" and keeps going,
 * rather than tearing the scanner down on the first frame after the camera opens.
 *
 * ## The fallback is a seam, not a dependency
 *
 * §6 names `@zxing/browser` as the fallback. `scanFrames` takes a `Detector`, and
 * `features/inventory/BarcodeScanner.tsx` takes the factory that builds one, so the
 * WASM/canvas decoder plugs in as a single function and everything else — the loop, the
 * checksum, the React component, the tests — stays as it is. v1 ships the seam with no
 * second decoder behind it: a browser without `BarcodeDetector` reports `unsupported`, and
 * §6's own fallback for that row is the manual-entry field the component already renders,
 * which is also the fallback for a denied camera. Adding the dependency is a follow-up;
 * see the BP-02.T5 hand-off note.
 *
 * ## The checksum is checked twice on purpose
 *
 * Rule 16 validates EAN-8/13 server-side and that is the authority. Checking it here too
 * means a misread frame — a partially-occluded barcode reads as a plausible-looking
 * number surprisingly often — never becomes a request, and the scanner can keep looking
 * instead of showing the operator a 404.
 */

/** The symbologies rule 16 stores in `sku.barcode`, in `BarcodeDetector`'s spelling. */
export const EAN_FORMATS = ['ean_13', 'ean_8'] as const;

export type EanFormat = (typeof EAN_FORMATS)[number];

/** The two lengths rule 16 accepts. */
export const EAN_LENGTHS: readonly number[] = [8, 13];

/** One decoded barcode — the subset of `DetectedBarcode` this app reads. */
export interface DetectedCode {
  rawValue: string;
  format: string;
}

/** What a detector does: decode whatever is in one frame. */
export interface Detector {
  detect: (source: CanvasImageSource) => Promise<DetectedCode[]>;
}

/** `new BarcodeDetector({ formats })`, plus its static capability query. */
export interface DetectorConstructor {
  new (options: { formats: readonly string[] }): Detector;
  getSupportedFormats: () => Promise<string[]>;
}

/** Builds a detector, or resolves `null` when this browser cannot scan. */
export type DetectorFactory = () => Promise<Detector | null>;

/** Why the scanner cannot run. Both end in §6's manual-entry field. */
export type ScannerProblem = 'unsupported' | 'denied' | 'unavailable';

/** The global object a lookup is done against; injected so a test can substitute one. */
export interface DetectorScope {
  BarcodeDetector?: DetectorConstructor;
}

/** The slice of `navigator.mediaDevices` the camera request uses. */
export interface MediaDevicesLike {
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
}

/** The rear-facing camera at a resolution a 13-digit EAN survives. */
export const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
  audio: false,
};

/** `{ stream }` when the camera opened, `{ problem }` when it did not. */
export type CameraResult = { stream: MediaStream; problem?: undefined } | { problem: ScannerProblem };

/**
 * Rule 16's check digit: `(10 − (weighted sum mod 10)) mod 10`, weights alternating
 * 3 and 1 from the right of the payload — GS1's definition for both lengths, and the
 * same arithmetic as `api/app/modules/catalogue/service.py`'s `validate_ean`.
 */
export function isValidEan(code: string): boolean {
  const cleaned = code.trim();
  if (!EAN_LENGTHS.includes(cleaned.length) || !/^\d+$/.test(cleaned)) {
    return false;
  }
  const payload = [...cleaned.slice(0, -1)].reverse();
  let weighted = 0;
  for (const [index, digit] of payload.entries()) {
    weighted += Number(digit) * (index % 2 === 0 ? 3 : 1);
  }
  return (10 - (weighted % 10)) % 10 === Number(cleaned.slice(-1));
}

/** The scanned code if it is a valid EAN-8/13, else `null`. */
export function normaliseScan(raw: string): string | null {
  const cleaned = raw.trim();
  return isValidEan(cleaned) ? cleaned : null;
}

/** `BarcodeDetector` from `scope`, or `null` when this browser has none. */
export function detectorConstructor(scope: DetectorScope): DetectorConstructor | null {
  return scope.BarcodeDetector ?? null;
}

/**
 * The platform detector, restricted to the formats it says it supports.
 *
 * `null` when the constructor is missing, when `getSupportedFormats()` names neither EAN
 * format, or when either call throws — a browser that advertises the API behind a flag
 * and then refuses is indistinguishable from one that lacks it, and both mean "fall back".
 */
export async function platformDetector(scope: DetectorScope): Promise<Detector | null> {
  const constructor = detectorConstructor(scope);
  if (constructor === null) {
    return null;
  }
  try {
    const supported = await constructor.getSupportedFormats();
    const formats = EAN_FORMATS.filter((format) => supported.includes(format));
    if (formats.length === 0) {
      return null;
    }
    return new constructor({ formats });
  } catch {
    return null;
  }
}

/**
 * Open the rear camera.
 *
 * `NotAllowedError` is the user saying no (§6's "camera denied → manual entry"); anything
 * else — no camera, a camera another app holds, an insecure context — is `unavailable`.
 * Both land on the same field, but the operator is told which happened.
 */
export async function openCamera(devices: MediaDevicesLike | undefined): Promise<CameraResult> {
  if (devices === undefined) {
    return { problem: 'unavailable' };
  }
  try {
    return { stream: await devices.getUserMedia(CAMERA_CONSTRAINTS) };
  } catch (error) {
    const name = error instanceof Error ? error.name : '';
    return { problem: name === 'NotAllowedError' ? 'denied' : 'unavailable' };
  }
}

/** Stop every track of `stream`, so the camera light goes out with the screen. */
export function closeCamera(stream: MediaStream | null): void {
  for (const track of stream?.getTracks() ?? []) {
    track.stop();
  }
}

/** How a scan loop is driven. Injected, so a test steps it instead of waiting. */
export interface Ticker {
  /** Run `frame` on the next tick; returns a handle for `cancel`. */
  schedule: (frame: () => void) => number;
  cancel: (handle: number) => void;
}

/** The browser's own frame clock. */
export function animationFrameTicker(): Ticker {
  return {
    schedule: (frame) => requestAnimationFrame(frame),
    cancel: (handle) => {
      cancelAnimationFrame(handle);
    },
  };
}

/** What a running scan loop offers its caller. */
export interface ScanLoop {
  stop: () => void;
}

export interface ScanLoopOptions {
  detector: Detector;
  /** The `<video>` (or canvas) the frames come from. */
  source: () => CanvasImageSource | null;
  /** Called with each *valid* EAN, at most once per distinct code in a row. */
  onCode: (code: string) => void;
  /** Explicitly `| undefined`, so a caller may forward an optional prop unchanged
      (`exactOptionalPropertyTypes` is on). */
  ticker?: Ticker | undefined;
}

/**
 * Read frames until `stop()`.
 *
 * Three deliberate behaviours:
 *
 * * a frame that rejects, or that the source is not ready for, is skipped — see the
 *   module docstring on `InvalidStateError`;
 * * a code that fails rule 16's checksum is skipped, so a misread never becomes a lookup;
 * * the same code twice in a row is reported once, because a barcode held in front of the
 *   camera decodes on every frame and the operator meant to add one line.
 */
export function scanFrames(options: ScanLoopOptions): ScanLoop {
  const ticker = options.ticker ?? animationFrameTicker();
  let running = true;
  let handle = 0;
  let last: string | null = null;

  const step = (): void => {
    if (!running) {
      return;
    }
    const source = options.source();
    if (source === null) {
      handle = ticker.schedule(step);
      return;
    }
    void options.detector
      .detect(source)
      .then((codes) => {
        for (const code of codes) {
          const value = normaliseScan(code.rawValue);
          if (value !== null && value !== last) {
            last = value;
            options.onCode(value);
          }
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (running) {
          handle = ticker.schedule(step);
        }
      });
  };

  handle = ticker.schedule(step);
  return {
    stop: () => {
      running = false;
      ticker.cancel(handle);
    },
  };
}
